import React from 'react';

const DEFAULT_PAGE_SIZES = [10, 25, 50, 100];

/**
 * Janela de páginas: primeira, última e as vizinhas da atual.
 * Retorna números e a string '…' para os buracos.
 */
const buildPageWindow = (page, totalPages) => {
  const window = [];
  for (let i = 1; i <= totalPages; i += 1) {
    const isEdge = i === 1 || i === totalPages;
    const isNear = Math.abs(i - page) <= 1;
    if (isEdge || isNear) {
      window.push(i);
    } else if (window[window.length - 1] !== '…') {
      window.push('…');
    }
  }
  return window;
};

/**
 * Rodapé de paginação padrão de todas as tabelas do sistema.
 *
 * Renderiza sempre a faixa "Mostrando X–Y de Z" (mesmo com uma página só, para
 * a tabela não "pular" de altura quando o filtro muda) e os botões de página
 * apenas quando há mais de uma.
 */
const Pagination = ({
  page,
  totalPages,
  total,
  from,
  to,
  onPageChange,
  onPageSizeChange,
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  itemLabel = 'registros',
  id = 'pagination',
}) => {
  if (!total) return null;

  const goTo = (target) => {
    const safe = Math.min(Math.max(target, 1), totalPages);
    if (safe !== page) onPageChange(safe);
  };

  return (
    <nav className="pagination-wrapper" aria-label="Paginação da tabela">
      <div className="pagination-left">
        {onPageSizeChange && (
          <div className="items-per-page">
            <label htmlFor={`${id}-size`}>Mostrar</label>
            <select
              id={`${id}-size`}
              className="items-select"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}
        <p className="pagination-info">
          <strong>{from}</strong>–<strong>{to}</strong> de <strong>{total}</strong> {itemLabel}
        </p>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            type="button"
            className="pagination-btn"
            onClick={() => goTo(page - 1)}
            disabled={page === 1}
            aria-label="Página anterior"
          >
            <span aria-hidden="true">‹</span> Anterior
          </button>

          <div className="pagination-numbers">
            {buildPageWindow(page, totalPages).map((entry, index) =>
              entry === '…' ? (
                <span key={`gap-${index}`} className="pagination-ellipsis" aria-hidden="true">
                  …
                </span>
              ) : (
                <button
                  key={entry}
                  type="button"
                  className={`pagination-number ${page === entry ? 'active' : ''}`}
                  onClick={() => goTo(entry)}
                  aria-current={page === entry ? 'page' : undefined}
                  aria-label={`Página ${entry}`}
                >
                  {entry}
                </button>
              ),
            )}
          </div>

          <button
            type="button"
            className="pagination-btn"
            onClick={() => goTo(page + 1)}
            disabled={page === totalPages}
            aria-label="Próxima página"
          >
            Próxima <span aria-hidden="true">›</span>
          </button>
        </div>
      )}
    </nav>
  );
};

export default Pagination;
